import { CSSProperties } from "react";

type GradientDirection =
  | "to-t"
  | "to-tr"
  | "to-r"
  | "to-br"
  | "to-b"
  | "to-bl"
  | "to-l"
  | "to-tl"
  | `${number}deg`;

const DIRECTION_MAP: Record<string, string> = {
  "to-t": "to top",
  "to-tr": "to top right",
  "to-r": "to right",
  "to-br": "to bottom right",
  "to-b": "to bottom",
  "to-bl": "to bottom left",
  "to-l": "to left",
  "to-tl": "to top left",
};

export function resolveColor(color: string): string {
  if (
    color === "transparent" ||
    color.startsWith("#") ||
    color.startsWith("rgb")
  ) {
    return color;
  }
  return `var(--color-${color})`;
}

/**
 * Returns a `className` and `style` for a gradient border that works with
 * border-radius AND a transparent interior. Uses the `border-gradient`
 * @utility defined in styles/base.css.
 *
 * Colors accept theme token names (`"primary-blue"`, `"new-gray-750"`) or raw
 * values (`"#333A42"`, `"rgb(0,0,0)"`).
 *
 * - `fill` is the interior background-color. Omit (or `"transparent"`) for a
 *   see-through interior; the masked ring keeps the gradient out of the middle.
 * - For a color → transparent fade, set `to: "transparent"`. It fades out by
 *   hue (the `from` color at 0 alpha) instead of through grey/black, which is
 *   what `linear-gradient(color, transparent)` does in sRGB.
 */

export type BorderGradientProps = {
  from?: string;
  to?: string;
  direction?: GradientDirection;
  width?: number;
  fill?: string;
};

export function borderGradient({
  from,
  to,
  direction = "to-b",
  width = 1,
  fill = "transparent",
}: BorderGradientProps): { className: string; style: CSSProperties } {
  const cssDir = DIRECTION_MAP[direction] ?? direction;
  const resolvedFrom = resolveColor(from);
  const resolvedTo = resolveColor(to);

  return {
    className: "border-gradient",
    style: {
      "--border-gradient-from": resolvedFrom,
      // Fade by hue, not to grey/black, when the end stop is transparent.
      "--border-gradient-to":
        resolvedTo === "transparent"
          ? `rgb(from ${resolvedFrom} r g b / 0)`
          : resolvedTo,
      "--border-gradient-direction": cssDir,
      "--border-gradient-width": `${width}px`,
      "--border-gradient-fill": resolveColor(fill),
    } as CSSProperties,
  };
}
